import { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useAuth } from 'react-oidc-context';

import { userPasswordActions } from '../../store/reducers/profile';

import { Avatar, Box, Button, Grid, Paper, Tab, Tabs, Tooltip, Typography, darken, lighten, useMediaQuery, useTheme } from '@mui/material';
import Styles from '../../theme/commonCss/Styles';
import DataTable from '../../components/DataTable';
import ChangePasswordForm from './changePasswordForm';
import PopUp from '../../components/Popup';
import { ReactComponent as LockIcon } from '../../assets/icons/main/lock-icon.svg';
import { ReactComponent as CheckIcon } from '../../assets/icons/main/check-icon.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/main/close-icon.svg';
import { ReactComponent as SuccessIcon } from '../../assets/icons/main/successIcon.svg';
import { DEFAULT_FEATURE_TITLES, DEFAULT_ROLES_ACCESS, getFeatureLabel } from '../../utils/user-management';
import { useOutletContext } from 'react-router-dom';
import { get } from 'lodash';
import { palette } from '../../theme/palette';

interface ProfileSettingsType {
  isUserAccessListLoading: boolean;
  userAccessList: any;
  isInvalidPassword: boolean;
  isUpdateUserPasswordSuccess: boolean;
  isUpdateUserPasswordLoading: boolean;
  isUpdateUserPasswordError: boolean;
  userAccess: any;
  userDetails: any;
  userRole: any;
  ontologyData: Array<any>;
  settingsData: any,
}

const styles = {
  userDetails: {
    paddingTop: '42px',
    borderRight: '1px solid #D9DFED',
    padding: '42px 32px 32px 32px',
    width: '324px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    wordWrap: 'break-word',
    textAlign: 'center',
  },
  avatar: {
    width: '60px',
    height: '60px',
    fontWeight: 600,
  },
};

const passwordFormData = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};


const TENANT_SETTINGS_ATTR = 'tenant/settings';

function Profile({
  userAccessList,
  isInvalidPassword,
  isUpdateUserPasswordSuccess,
  isUpdateUserPasswordLoading,
  isUpdateUserPasswordError,
  userAccess,
  userDetails,
  userRole,
  settingsData,
  ontologyData,
}: ProfileSettingsType) {
  const themeData: any = useTheme();
  const themeMode: any = get(useOutletContext(), 'themeMode', {});
  const auth = useAuth();
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState(0);
  const handleTabChange = (e: any, value: any) => {
    setSelectedTab(value);
  };
  const [isOpen, setIsOpen] = useState(false);
  const [passwordData, setPasswordData] = useState(passwordFormData);

  const responsiveSize = useMediaQuery('(max-width:900px)');
  const containerSize =  useMediaQuery('(min-width: 900px)');
  const handleClick = () => {
    setIsOpen(true);
  };

  const handlePopup = () => {
    dispatch(userPasswordActions.resetLoadingState());
  };

  const renderStatus = (params: any) => {
    const status = params.value;
    return status ? <CheckIcon style={{ color: palette.success.dark }} /> : <CloseIcon style={{ color: palette.error.main }} />;
  };


  const columns = [
    {
      field: 'object_name',
      headerName: 'Features',
      flex: 1,
      minWidth: 380,
      maxWidth: 380,
      renderCell: (params: any) => {
        const featureName = params.row.route_id === 'site_factory' && getFeatureLabel(settingsData, params.row) === 'Shop Floor Configuration' ? (ontologyData?.slice(-1)?.[0]?.level_name ?? 'Shop floor') + ' Configuration' : getFeatureLabel(settingsData, params.row)

        return (
          <Tooltip title={featureName} placement='left' arrow>
            <Typography
              component='p'
              variant='subtitle1'
              sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
              {featureName}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'read',
      headerName: 'READ',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 120,
      renderCell: renderStatus,
    },
    {
      field: 'create',
      headerName: 'CREATE',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 120,
      renderCell: renderStatus,
    },
    {
      field: 'update',
      headerName: 'UPDATE',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 120,
      renderCell: renderStatus,
    },
    {
      field: 'delete',
      headerName: 'DELETE',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 120,
      renderCell: renderStatus,
    },
    {
      field: 'approver',
      headerName: 'Approve',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 150,
      renderCell: renderStatus,
    },
  ];

  const closeModal = () => {
    setIsOpen(false);
    setPasswordData(passwordFormData);
  };

  useEffect(() => {
    if (isUpdateUserPasswordSuccess) {
      closeModal();
    }
  }, [isUpdateUserPasswordSuccess]);

  const stringAvatar: any = (name: string) => {
    return {
      children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
    };
  };

  const renderModal = () => (
    <ChangePasswordForm
      isOpen={isOpen}
      passwordData={passwordData}
      setPasswordData={setPasswordData}
      handleClose={closeModal}
      userEmail={userDetails.email}
      isInvalidPassword={isInvalidPassword}
      isUpdateUserPasswordLoading={isUpdateUserPasswordLoading}
      isUpdateUserPasswordError={isUpdateUserPasswordError}
    />
  );

  const userTenant: any = auth?.user?.profile?.tenant;

  const tenant = (userTenant || []).map((tenant: any) => tenant.replace('/', ' '));
  const authUserTenant = tenant.length > 1 ? tenant.join(',') : tenant;

  const userData: any = [
    { id: 1, label: 'First Name', value: 'firstName', bg: (themeMode.isLightBG) ? lighten(themeData.palette.background.default, 0.2) : lighten(themeData.palette.background.default, 0.08), textcapital: true },
    { id: 2, label: 'Last Name', value: 'lastName', bg: (themeMode.isLightBG) ? lighten(themeData.palette.background.default, 0.2) : lighten(themeData.palette.background.default, 0.08), textcapital: true },
    { id: 3, label: 'Role', value: 'role', bg: (themeMode.isLightBG) ? lighten(themeData.palette.background.default, 0.9) : lighten(themeData.palette.background.default, 0.05), textcapital: true },
    { id: 4, label: 'Email', value: 'email', bg: (themeMode.isLightBG) ? lighten(themeData.palette.background.default, 0.9) : lighten(themeData.palette.background.default, 0.05), textcapital: false },
    { id: 5, label: 'Tenant', value: 'tenant', bg: (themeMode.isLightBG) ? lighten(themeData.palette.background.default, 0.2) : lighten(themeData.palette.background.default, 0.08), textcapital: true },
    { id: 6, label: '', value: '', bg: (themeMode.isLightBG) ? lighten(themeData.palette.background.default, 0.2) : lighten(themeData.palette.background.default, 0.08), textcapital: false },
  ];

  return (
    <>
      <Box sx={{...Styles.masterContainer, paddingRight : containerSize ? '8px' : 0}}>
        <Paper sx={{
          ...Styles.masterPaperContainer, border: `1px solid  ${themeData.palette.customBorderColor.main}`,
          //  backgroundColor:lighten(themeData.palette.background.default,0.3)
          // backgroundColor: lighten(themeData.palette.background.default, 0.2)//for lighter shade
          backgroundColor: (themeMode.isLightBG) ? lighten(themeData.palette.background.default, 0.9) : lighten(themeData.palette.background.default, 0.05),
        }}>
          <Grid container>
            <Grid item xs={12} sm={12} md={3} lg={2} sx={{ ...styles.userDetails, borderRight: `1px solid  ${themeData.palette.customBorderColor.main}` }}>
              <Avatar {...stringAvatar(auth.user?.profile?.name?.toUpperCase())}
                sx={{
                  ...styles.avatar,
                  color: themeData.palette.text.primary,
                  backgroundColor: (themeMode.isLightBG) ? darken(themeData.palette.background.default, 0.1) : lighten(themeData.palette.background.default, 0.08)
                }} />
              <Typography variant='subtitle2'
                color={themeData.palette.text.primary} //'#434D67'
                sx={{ paddingTop: '16px', textTransform: 'capitalize' }}>
                {auth.user?.profile.name}
              </Typography>
              <Typography
                sx={{
                  paddingTop: '6px',
                  color: themeData.palette.text.secondary,//'#64759F',
                  fontWeight: 400,
                  fontSize: '0.9rem',
                  textTransform: 'capitalize',
                }}
              >
                {userRole.name}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
              md={9}
              lg={10}
              sx={{
                minHeight: 'calc(100vh - 184px)',
                overflow: 'hidden',
                width: '100%',
              }}
            >
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTabs-scroller': { paddingLeft: '16px' },
                }}
              >
                <Tab label='Account Setting' />
                <Tab label='Access Info' />
              </Tabs>
              {selectedTab === 0 && (
                <>
                  <Grid
                    container
                    alignItems='center'
                    height='106px'
                    justifyContent='end'
                    sx={{ borderTop: `1px solid  ${themeData.palette.customBorderColor.main}`, borderBottom: `1px solid  ${themeData.palette.customBorderColor.main}` }}
                  >
                    <Grid item sx={{ paddingRight: '32px' }}>
                      <Button variant='contained' onClick={handleClick}>
                        <LockIcon />
                        Change Password
                      </Button>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} sx={{ marginTop: '0', marginLeft: '0', width: '100%' }}>
                    {userData.map((user: any) => (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={6}
                        key={user.id}
                        sx={{
                          display: user.id === 6 && responsiveSize ? 'none' : 'flex',
                          alignItems: 'center',
                          paddingBlock: 2,
                          backgroundColor: user.bg,
                          borderBottom: `1px solid  ${themeData.palette.customBorderColor.main}`,
                          overflow: 'hidden',
                        }}
                      >
                        {user.id < 6 ? (
                          <>
                            <Grid
                              item
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                pl: 2,
                              }}
                            >
                              <Typography variant='subtitle1' color={themeData.palette.text.secondary} sx={{ whiteSpace: 'nowrap' }}>
                                {user.label}
                              </Typography>
                              <span style={{ color: themeData.palette.text.secondary }}>:</span>
                            </Grid>
                            <Grid item xs={12} sm={12} sx={{ overflow: 'hidden' }}>
                              <span
                                style={{
                                  paddingLeft: '4px',
                                  color: themeData.palette.text.primary,//'#434D67',
                                  textTransform: user.textcapital ? 'capitalize' : 'none',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  display: 'block',
                                }}
                              >
                                {user.value === 'role'
                                  ? userRole.name
                                  : user.value === 'tenant'
                                    ? authUserTenant
                                    : userDetails[user.value]}
                              </span>
                            </Grid>
                          </>
                        ) : (
                          ''
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
              {selectedTab === 1 && (
                <div>
                  <Grid
                    container
                    justifyContent='space-between'
                    alignItems='center'
                    height='106px'
                    sx={{ borderTop: `1px solid  ${themeData.palette.customBorderColor.main}`, borderBottom: `1px solid  ${themeData.palette.customBorderColor.main}` }}
                  >
                    <Grid item justifyContent='flex-start'>
                      <Typography variant='h2' color={themeData.palette.text.priamry} sx={{ paddingLeft: '32px' }}>
                        Access Control
                      </Typography>
                    </Grid>
                  </Grid>
                  <DataTable
                    columns={columns}
                    rows={DEFAULT_ROLES_ACCESS.map((item: any, idx: number) => ({
                      ...item,
                      ...userAccess[item.object_name.trim()],
                    })).filter((row: any) => {
                      if (row.object_name === TENANT_SETTINGS_ATTR) {
                        const loggedUserTenantSettings = get(userAccess, TENANT_SETTINGS_ATTR, {});
                        return (
                          loggedUserTenantSettings.read &&
                          loggedUserTenantSettings.create &&
                          loggedUserTenantSettings.delete &&
                          loggedUserTenantSettings.update
                        );
                      }
                      return true;
                    })}
                    // rows={Object.keys(userAccess).map((item: string, idx: number) => ({
                    //   id: idx,
                    //   object_name: item,
                    //   ...userAccess[item],
                    // }))}
                    // tableHeight='calc(100vh - 450px)'
                    hideFooter
                    pageSize={17}
                  />
                </div>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Box>
      {isUpdateUserPasswordSuccess && (
        <PopUp
          title='Success'
          open={isUpdateUserPasswordSuccess}
          icon={<SuccessIcon style={{ color: palette.success.dark }} />}
          message='Your password has been changed.'
          confirmation
          confirmLabel='OK'
          onConfirm={handlePopup}
          onClose={handlePopup}
        />
      )}
      {isOpen && renderModal()}
    </>
  );
}

const mapStateToProps = (state: any) => ({
  isUserAccessListLoading: state.userManagement.isUserAccessListLoading,
  userAccessList: state.userManagement.userAccessList,
  isInvalidPassword: state.profile.isInvalidPassword,
  isUpdateUserPasswordSuccess: state.profile.isUpdateUserPasswordSuccess,
  isUpdateUserPasswordLoading: state.profile.isUpdateUserPasswordLoading,
  isUpdateUserPasswordError: state.profile.isUpdateUserPasswordError,
  userAccess: state.profile.userAccess,
  userDetails: state.profile.userDetails,
  userRole: state.profile.userRole,
  ontologyData: state.ontologyMaster.ontologyData,
  settingsData: state.settings.settingsData,
});

export default connect(mapStateToProps)(Profile);
